<!-- src/js/widgets/manager-residency-requests/index.vue -->
<template>
  <div class="manager-residency-requests">
    <RequestsList
      v-if="currentView === 'list'"
      :requests="requests.data"
      :pagination="requests.pagination"
      :loading="loading"
      :filter="{ ...filter, filtersLoading, filtersLoadingError }"
      @params-change="handleParamsChange"
      @row-click="openRequest"
      @export-click="handleExport"
    />

    <RequestDetail
      v-else-if="currentView === 'detail' && selectedRequest"
      :request="selectedRequest"
      :loading="loading"
      ref="requestDetailRef"
      @close="closeDetail"
      @status-updated="onStatusUpdated"
      @reply="onSubmitReply"
      @back="goBack"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { Request, PermissionFormData, FiltersListResponse, ApiStatusResponse, RawApplication, UpdateStatusPayload } from './types';
import { useRequestData } from './composables/useRequestData';
import { useRequestFilter } from './composables/useRequestFilter';
import RequestsList from './components/RequestsList.vue';
import RequestDetail from './components/RequestDetail.vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';

const selectedRequest = ref<Request | null>(null);
const currentView = ref<'list' | 'detail'>('list');

const {
  requests,
  loading,
  error,
  loadRequests,
  loadRequestDetails,
  updateStatus,
  sendReply,
  exportRequests,
} = useRequestData();

const { filter, loading: filtersLoading, error: filtersLoadingError } = useRequestFilter();

watch(error, (err) => {
  if (err) {
    showStcErrorNotification(err);
  }
});

async function handleParamsChange(params: {
  page: number;
  filter: Partial<Record<keyof FiltersListResponse, string>> & {
    dateFrom: string | null;
    dateTo: string | null;
  };
  search: string;
}) {
  await loadRequests(params);
}

async function handleExport(params: {
  filter: Partial<Record<keyof FiltersListResponse, string>> & {
    dateFrom: string | null;
    dateTo: string | null;
  };
  search: string;
}) {
  await exportRequests(params);
}

async function openRequest(request: RawApplication): Promise<void> {
  const fullRequest = await loadRequestDetails(request.id);
  if (fullRequest) {
    selectedRequest.value = fullRequest;
    currentView.value = 'detail';
  } else {
    showStcErrorNotification('Не удалось загрузить детали заявки');
  }
}

function closeDetail(): void {
  selectedRequest.value = null;
}

function goBack() {
  currentView.value = 'list';
  selectedRequest.value = null;
}

async function onSubmitReply({ comment, files }: PermissionFormData): Promise<void> {
  if (!selectedRequest.value?.id) return;

  try {
    await sendReply(selectedRequest.value.id, { comment, files });
    showStcSuccessNotification('Ответ успешно отправлен');
  } catch {
    showStcErrorNotification('Не удалось отправить ответ');
  }
}

async function onStatusUpdated({
  status,
  text,
}: {
  status?: ApiStatusResponse;
  text?: string;
}): Promise<void> {
  if (!selectedRequest.value) return;
  const requestParams: UpdateStatusPayload = {};
  if (status?.id) {
    requestParams.status = status;
  }
  if (text) {
    requestParams.admin_comment = text;
  }

  try {
    await updateStatus(selectedRequest.value.id, requestParams);
    showStcSuccessNotification('Данные успешно сохранены');
    const updatedRequest = await loadRequestDetails(selectedRequest.value.id);
    if (updatedRequest) {
      selectedRequest.value = updatedRequest;
    }
  } catch (err) {
    showStcErrorNotification('Ошибка обновления статуса');
    console.error('Ошибка обновления статуса:', err);
  }
}
</script>
